import { useState, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { RESIDENCY_DOC_TYPES } from '../../../../mocks/cases';
import type { DocumentReviewStatus } from '../../../../mocks/cases';
import CategoryHeader from './CategoryHeader';
import type { DocState } from './types';
import { randomSize, reviewStatusBadgeCls, reviewStatusLabel } from './utils';
import { DOCUMENT_FILE_ACCEPT } from '../../../../utils/documentUploadPolicy';

const RESIDENCE_DOC_LABELS: Record<string, string> = {
  padron_municipal_registration_id: 'Padrón / Municipal registration',
  rental_contract_id: 'Rental contract',
  medical_record_id: 'Medical record',
  childrens_school_enrollment_id: "Children's school enrollment",
  bank_statement_id: 'Bank statement',
  utility_bill_id: 'Utility bill',
  payslip_work_contract_id: 'Payslip or work contract',
  money_transfer_receipt_id: 'Money transfer receipt',
  course_training_certificate_id: 'Course or training certificate',
  transport_ticket_id: 'Transport ticket',
  police_report_id: 'Police report',
  official_letter_notification_id: 'Official letter or notification',
  other: 'Other',
  Padron: 'Padrón / Municipal registration',
  Rental: 'Rental contract',
  Medical: 'Medical record',
  School: "Children's school enrollment",
  Bank: 'Bank statement',
  Utility: 'Utility bill',
  Payslip: 'Payslip or work contract',
  Transfer: 'Money transfer receipt',
  Course: 'Course or training certificate',
  Transport: 'Transport ticket',
  Police: 'Police report',
  Official: 'Official letter or notification',
  Other: 'Other',
};

interface Props {
  docs: DocState[];
  onDocsChange: (next: DocState[]) => void;
  categoryReview: DocumentReviewStatus;
  onCategoryReviewChange: (status: DocumentReviewStatus) => void;
  onViewDocument?: (doc: DocState) => void;
}

export default function ResidenceCategory({ docs, onDocsChange, categoryReview, onCategoryReviewChange, onViewDocument }: Props) {
  const { t } = useTranslation();
  const [newDocType, setNewDocType] = useState('');
  const fileRef = useRef<HTMLInputElement>(null);
  const getResidenceDocLabel = (value?: string, supportingDescription?: string) => {
    const baseLabel =
      (value ? RESIDENCE_DOC_LABELS[value] : '') ||
      RESIDENCY_DOC_TYPES.find((type) => type.value === value)?.label ||
      value ||
      '';
    const extra = String(supportingDescription || '').trim();
    const normalized = String(value || '').trim().toLowerCase();
    const isOther = normalized === 'other' || normalized === 'other_supporting';
    if (isOther && extra) return `${baseLabel}: ${extra}`;
    return baseLabel;
  };

  const catDocs = docs.filter((d) => d.category === 'residence' && d.uploaded);

  return (
    <div className="max-w-[720px]">
      <CategoryHeader title={t('advisorPortal.docCat_proof_of_residency_name')} category="residence" status={categoryReview} onChange={onCategoryReviewChange} />

      {/* Uploaded list */}
      {catDocs.length > 0 && (
        <div className="mb-3 space-y-1.5 max-w-[480px]">
          {catDocs.map((doc) => (
            <div key={doc.id} className="bg-white rounded-xl border border-slate-100 px-4 py-3 flex flex-col gap-3 min-[420px]:flex-row min-[420px]:items-center">
              <i className="ri-file-text-line text-slate-500 text-lg shrink-0" />
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2">
                  <p className="min-w-0 text-[13px] font-medium text-slate-700 truncate">{doc.uploadedFile ?? doc.name}</p>
                  <span className={`shrink-0 rounded-full px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide ${reviewStatusBadgeCls(doc.reviewStatus)}`}>
                    {reviewStatusLabel(doc.reviewStatus)}
                  </span>
                </div>
                <p className="text-[11px] text-slate-500">
                  {getResidenceDocLabel(doc.docType, doc.supportingDescription)}
                </p>
              </div>
              <button
                type="button"
                className="min-h-[36px] px-3 rounded-lg border border-slate-200 text-[12px] font-medium text-slate-600 hover:bg-slate-50 cursor-pointer shrink-0"
                onClick={() => onViewDocument?.(doc)}
              >
                {t('advisorPortal.docPreview', { defaultValue: 'View' })}
              </button>
              <button
                type="button"
                className="w-9 h-9 flex items-center justify-center text-slate-300 hover:text-slate-500 cursor-pointer shrink-0 transition-colors"
                onClick={() => onDocsChange(docs.filter((d) => d.id !== doc.id))}
              >
                <i className="ri-close-line" />
              </button>
            </div>
          ))}
        </div>
      )}

      {/* Always-visible capture panel */}
      <div className="max-w-[480px]">
        <label className="block text-[13px] font-semibold text-slate-900 mb-1.5">Document type</label>
        <select
          value={newDocType}
          onChange={(e) => setNewDocType(e.target.value)}
          className={`w-full min-h-[44px] px-3 border border-slate-200 rounded-xl text-sm bg-white mb-3 ${!newDocType ? 'text-slate-400' : 'text-slate-700'}`}
        >
          <option value="">Select...</option>
          {RESIDENCY_DOC_TYPES.map((opt) => (
            <option key={opt.value} value={opt.value}>{opt.label}</option>
          ))}
        </select>

        <input
          ref={fileRef}
          type="file"
          accept={DOCUMENT_FILE_ACCEPT}
          onChange={(e) => {
            const file = e.target.files?.[0];
            if (!file || !newDocType) return;
            onDocsChange([...docs, {
              id: `res-${Date.now()}`,
              name: file.name,
              category: 'residence',
              docType: newDocType,
              uploaded: true,
              uploadedAt: new Date().toISOString().slice(0, 10),
              uploadedFile: file.name,
              rawFile: file,
              reviewStatus: 'pending' as DocumentReviewStatus,
              fileSize: randomSize(),
            }]);
            setNewDocType('');
            if (fileRef.current) fileRef.current.value = '';
          }}
          className="hidden"
        />

        <div className="flex">
          <button
            type="button"
            onClick={() => fileRef.current?.click()}
            disabled={!newDocType}
            className="w-full min-h-[44px] bg-white border border-slate-200 rounded-lg flex items-center justify-center gap-2 text-sm text-slate-600 hover:border-slate-300 hover:bg-slate-50 transition-colors cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <i className="ri-file-upload-line" /> {t('docUpload.chooseFile', { defaultValue: 'Choose file' })}
          </button>
        </div>
      </div>
    </div>
  );
}
